import { Box, Stack } from "@mui/material";
import { Button, Typography } from "@platform/design-system";
import { CellValueChangedEvent, GridApi } from "ag-grid-community";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  useBatchUpdateBenefitClasses,
  useBenefitClasses,
  useCreateBenefitClass,
  useDeleteBenefitClass,
} from "../../api/benefitClasses";
import { useRfpCoverages } from "../../api/rfp";
import useToastNotificationService from "../../hooks/useToastNotificationService";

import BenefitsClassGrid, { BenefitsClassRow } from "./BenefitsClassGrid";
import BenefitsClassToolbar from "./BenefitsClassToolbar";
import { CenteredBox } from "./CenteredBox";
import {
  isCreatableClassName,
  transformToApiFormat,
  transformToGridData,
} from "./transforms";

// Generate a temporary ID for new rows
const generateTempId = (): string =>
  `temp-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

const COVERAGE_TYPE_TO_FIELD: Record<string, string> = {
  dental: "dental",
  vision: "vision",
  std: "std",
  ltd: "ltd",
  voluntaryLifeAdnd: "voluntaryLife",
  eap: "eap",
  hospitalIndemnity: "hi",
  basicLifeAdnd: "basicLife",
  criticalIllness: "ci",
  accident: "accident",
  cancer: "cancer",
  individualDisabilityInsurance: "idi",
  fmla: "fmla",
  wholeLife: "wholeLife",
};

const COVERAGE_FIELDS = Object.values(COVERAGE_TYPE_TO_FIELD);

/**
 * Defines the desired display order for coverage types
 * Coverage types not in this list will appear at the end in their natural order
 */
const COVERAGE_SORT_ORDER: Record<string, number> = {
  dental: 0,
  vision: 1,
  std: 2,
  ltd: 3,
  basicLifeAdnd: 4,
  voluntaryLifeAdnd: 5,
  accident: 6,
  criticalIllness: 7,
  hospitalIndemnity: 8,
  cancer: 9,
  wholeLife: 10,
  fmla: 11,
  individualDisabilityInsurance: 12,
  eap: 13,
};

interface ClassManagerTabProps {
  ecoRfpId: string;
  onClose: VoidFunction;
}

const ClassManagerTab = ({
  ecoRfpId,
  onClose,
}: ClassManagerTabProps): React.JSX.Element => {
  const [selectedRows, setSelectedRows] = useState<BenefitsClassRow[]>([]);
  const gridApiRef = useRef<GridApi | null>(null);
  const [gridData, setGridData] = useState<BenefitsClassRow[]>([]);
  const { createToastNotification } = useToastNotificationService();

  // Fetch benefit classes from API
  const {
    data: benefitClasses = [],
    isLoading,
    error,
    refetch,
  } = useBenefitClasses(ecoRfpId || "", { enabled: !!ecoRfpId });

  // Fetch RFP coverages - this is the source of truth for available coverage types and their IDs
  const {
    data: rfpCoverages = [],
    isLoading: coveragesLoading,
    isFetching: coveragesFetching,
  } = useRfpCoverages(ecoRfpId || "", {
    enabled: !!ecoRfpId,
    staleTime: 0,
    select: (data) =>
      // Sort coverages based on defined order, with unknown types at the end
      [...data].sort(
        (a, b) =>
          (COVERAGE_SORT_ORDER[a.type] ?? Infinity) -
          (COVERAGE_SORT_ORDER[b.type] ?? Infinity),
      ),
  });

  // Refetch data when drawer opens (on mount or when ecoRfpId changes)
  useEffect(() => {
    if (ecoRfpId) {
      refetch();
    }
  }, [ecoRfpId, refetch]);

  // Batch update mutation
  const batchUpdateMutation = useBatchUpdateBenefitClasses(ecoRfpId || "");

  // Create benefit class mutation
  const createClassMutation = useCreateBenefitClass(ecoRfpId || "", {
    onSuccess: (newClass) => {
      // Replace temp ID with real ID from backend
      if (!gridApiRef.current) return;

      const updatedData: BenefitsClassRow[] = [];
      gridApiRef.current.forEachNode((node) => {
        if (node.data) {
          // Find the temp row and replace its ID
          if (
            node.data.id.startsWith("temp-") &&
            node.data.className === newClass.name
          ) {
            updatedData.push({
              ...node.data,
              id: newClass.id,
            });
          } else {
            updatedData.push(node.data);
          }
        }
      });

      gridApiRef.current.setGridOption("rowData", updatedData);
      setGridData(updatedData);
    },
  });

  // Delete benefit class mutation
  const deleteClassMutation = useDeleteBenefitClass(ecoRfpId || "", {
    onSuccess: () => {
      // Clear selection after delete
      setSelectedRows([]);

      // Proactively remove deleted rows from grid
      if (!gridApiRef.current) return;

      const deletedIds = new Set(selectedRows.map((row) => row.id));
      const updatedData: BenefitsClassRow[] = [];

      gridApiRef.current.forEachNode((node) => {
        if (node.data && !deletedIds.has(node.data.id)) {
          updatedData.push(node.data);
        }
      });

      gridApiRef.current.setGridOption("rowData", updatedData);
      setGridData(updatedData);
    },
  });

  // Transform API data to grid format
  const rowData = useMemo(() => {
    if (!benefitClasses.length) return [];
    return transformToGridData(benefitClasses);
  }, [benefitClasses]);

  // Initialize gridData state with transformed API data (only when benefitClasses changes)
  useEffect(() => {
    if (benefitClasses.length > 0) {
      setGridData(transformToGridData(benefitClasses));
    }
  }, [benefitClasses]);

  // Use RFP coverages as the source of truth for coverage type -> ID mapping
  // This ensures we always have the coverage IDs available, even when unchecking/checking
  const allCoverages = useMemo(
    () =>
      // Transform RfpCoverageResponse to CoverageResponse format expected by transforms
      rfpCoverages.map((coverage) => ({
        id: coverage.id,
        type: coverage.type,
        createdAt: coverage.createdAt,
        updatedAt: coverage.updatedAt,
      })),
    [rfpCoverages],
  );

  const [classesWithoutCoverages, hasEmptyClassNames] = useMemo<
    [string[], boolean]
  >(() => {
    if (!gridData.length || !rfpCoverages.length) return [[], false];

    // Get all coverage field names from RFP coverages
    const coverageFields = rfpCoverages.map(
      (coverage) => COVERAGE_TYPE_TO_FIELD[coverage.type] || coverage.type,
    );

    const invalidClassIds = gridData.reduce<string[]>((ids, row) => {
      // Check if this class has at least one coverage assigned
      const hasAtLeastOneCoverage = coverageFields.some(
        (field) => row[field] === true,
      );
      if (!hasAtLeastOneCoverage) {
        return [...ids, row.id];
      }
      return ids;
    }, []);

    const hasInvalidClassNames = gridData.some((row) => !row.className?.trim());

    return [invalidClassIds, hasInvalidClassNames];
  }, [gridData, rfpCoverages]);

  const isValid = classesWithoutCoverages.length === 0 && !hasEmptyClassNames;

  const handleGridReady = (api: GridApi): void => {
    gridApiRef.current = api;
  };

  const handleSelectionChange = (rows: BenefitsClassRow[]): void => {
    setSelectedRows(rows);
  };

  const handleCellValueChanged = (
    event?: CellValueChangedEvent<BenefitsClassRow>,
  ): void => {
    // Sync gridData state when user edits cells directly
    if (!gridApiRef.current) return;

    const currentData: BenefitsClassRow[] = [];
    gridApiRef.current.forEachNode((node) => {
      if (node.data) {
        currentData.push(node.data);
      }
    });

    setGridData(currentData);

    // If a temp row's className was edited and now has a value, create it in the backend
    if (
      event?.data &&
      event.data.id.startsWith("temp-") &&
      event.colDef.field === "className"
    ) {
      const className = event.data.className?.trim();

      if (className && isCreatableClassName(className)) {
        createClassMutation.mutate({
          rfpId: ecoRfpId || "",
          benefitClass: {
            name: className,
            coverageIds: [],
          },
        });
      }
    }
  };

  const handleDelete = (): void => {
    // Delete all selected classes
    selectedRows.forEach((row) => {
      deleteClassMutation.mutate(row.id);
    });
  };

  const handleClearCoverages = (): void => {
    if (!gridApiRef.current) return;

    const selectedIds = new Set(selectedRows.map((row) => row.id));
    const updatedData: BenefitsClassRow[] = [];

    gridApiRef.current.forEachNode((node) => {
      if (node.data) {
        if (selectedIds.has(node.data.id)) {
          updatedData.push({
            ...node.data,
            ...COVERAGE_FIELDS.reduce(
              (acc, field) => ({ ...acc, [field]: false }),
              {},
            ),
          });
        } else {
          updatedData.push(node.data);
        }
      }
    });

    gridApiRef.current.setGridOption("rowData", updatedData);
    setGridData(updatedData);
  };

  const handleAssignAllCoverages = (): void => {
    if (!gridApiRef.current) return;

    const selectedIds = new Set(selectedRows.map((row) => row.id));
    const updatedData: BenefitsClassRow[] = [];

    gridApiRef.current.forEachNode((node) => {
      if (node.data) {
        if (selectedIds.has(node.data.id)) {
          updatedData.push({
            ...node.data,
            ...COVERAGE_FIELDS.reduce(
              (acc, field) => ({ ...acc, [field]: true }),
              {},
            ),
          });
        } else {
          updatedData.push(node.data);
        }
      }
    });

    gridApiRef.current.setGridOption("rowData", updatedData);
    setGridData(updatedData);
  };

  const handleCopy = (): void => {
    navigator.clipboard.writeText(
      "Your organization has disabled the copying of this data",
    );
  };

  const handleAssignAllCoveragesForAllRows = (): void => {
    if (!gridApiRef.current) return;

    const allCoveragesPatch = COVERAGE_FIELDS.reduce<Record<string, boolean>>(
      (acc, field) => ({ ...acc, [field]: true }),
      {},
    );
    const updatedData: BenefitsClassRow[] = [];

    gridApiRef.current.forEachNode((node) => {
      if (node.data) {
        updatedData.push({
          ...node.data,
          ...allCoveragesPatch,
        });
      }
    });

    gridApiRef.current.setGridOption("rowData", updatedData);
    setGridData(updatedData);
  };

  const handleClearCoveragesForAllRows = (): void => {
    if (!gridApiRef.current) return;

    const noCoveragesPatch = COVERAGE_FIELDS.reduce<Record<string, boolean>>(
      (acc, field) => ({ ...acc, [field]: false }),
      {},
    );
    const updatedData: BenefitsClassRow[] = [];

    gridApiRef.current.forEachNode((node) => {
      if (node.data) {
        updatedData.push({
          ...node.data,
          ...noCoveragesPatch,
        });
      }
    });

    gridApiRef.current.setGridOption("rowData", updatedData);
    setGridData(updatedData);
  };

  const handleClearSelection = (): void => {
    if (!gridApiRef.current) return;
    gridApiRef.current.deselectAll();
  };

  const handleHeaderCheckboxClick = (
    field: string,
    newValue: boolean,
  ): void => {
    if (!gridApiRef.current) return;

    // Update all rows with the new value for this field
    const updatedData: BenefitsClassRow[] = [];

    gridApiRef.current.forEachNode((node) => {
      if (node.data) {
        updatedData.push({
          ...node.data,
          [field]: newValue,
        });
      }
    });

    gridApiRef.current.setGridOption("rowData", updatedData);
    setGridData(updatedData);
  };

  const handleAddClass = (): void => {
    if (!gridApiRef.current) return;

    // Map coverage types to field names (must match COVERAGE_TYPE_TO_FIELD in BenefitsClassGrid)
    const COVERAGE_TYPE_TO_FIELD: Record<string, string> = {
      dental: "dental",
      vision: "vision",
      std: "std",
      ltd: "ltd",
      voluntaryLifeAdnd: "voluntaryLife",
      eap: "eap",
      hospitalIndemnity: "hi",
      basicLifeAdnd: "basicLife",
      criticalIllness: "ci",
      accident: "accident",
      cancer: "cancer",
      individualDisabilityInsurance: "idi",
    };

    // Create a new row with temp ID and dynamically build coverage fields from rfpCoverages
    const newRow: Partial<BenefitsClassRow> = {
      id: generateTempId(),
      className: "",
    };

    // Add all coverage fields based on what's actually in the RFP
    rfpCoverages.forEach((coverage) => {
      const field = COVERAGE_TYPE_TO_FIELD[coverage.type] || coverage.type;
      newRow[field] = false;
    });

    // Add row to grid
    const currentData: BenefitsClassRow[] = [];
    gridApiRef.current.forEachNode((node) => {
      if (node.data) {
        currentData.push(node.data);
      }
    });

    const updatedData = [...currentData, newRow as BenefitsClassRow];
    gridApiRef.current.setGridOption("rowData", updatedData);
    setGridData(updatedData);

    // Focus and start editing the className cell after a brief delay
    setTimeout(() => {
      if (!gridApiRef.current) return;
      const rowIndex = updatedData.length - 1;
      gridApiRef.current.startEditingCell({
        rowIndex,
        colKey: "className",
      });
    }, 100);
  };

  const handleSave = async (): Promise<void> => {
    if (!gridApiRef.current) return;

    // Commit any in-progress cell edit before collecting data
    gridApiRef.current.stopEditing();

    // Collect current row data from grid
    const currentRowData: BenefitsClassRow[] = [];
    gridApiRef.current.forEachNode((node) => {
      if (node.data) {
        currentRowData.push(node.data);
      }
    });

    // Transform to API format and save
    const updates = transformToApiFormat(currentRowData, allCoverages);

    const isInvalid = updates.some(
      (update) => (update.name?.trim() ?? "").length === 0,
    );

    if (isInvalid) {
      createToastNotification({
        severity: "error",
        message: "Class names cannot be empty. Please fix before saving.",
      });
      return;
    }

    try {
      await batchUpdateMutation.mutateAsync({ updates });
      // Success - close drawer
      onClose();
    } catch {
      // Error is logged by the API client
      // Could show error UI here if needed
    }
  };

  if (isLoading || coveragesLoading || coveragesFetching) {
    return (
      <CenteredBox>
        <Typography>Loading benefit classes...</Typography>
      </CenteredBox>
    );
  }

  if (error) {
    return (
      <CenteredBox>
        <Typography color="danger">
          Error loading benefit classes: {error.message}
        </Typography>
      </CenteredBox>
    );
  }

  return (
    <>
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          px: 3,
        }}
      >
        <BenefitsClassToolbar
          selectedCount={selectedRows.length}
          onDelete={handleDelete}
          onClearCoverages={handleClearCoverages}
          onAssignAllCoverages={handleAssignAllCoverages}
          onCopy={handleCopy}
          onClearSelection={handleClearSelection}
          onAddClass={handleAddClass}
          onAssignAllCoveragesForAllRows={handleAssignAllCoveragesForAllRows}
          onClearCoveragesForAllRows={handleClearCoveragesForAllRows}
          classesWithoutCoverages={classesWithoutCoverages}
        />
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Box sx={{ height: "100%", minWidth: "fit-content" }}>
            <BenefitsClassGrid
              rowData={rowData}
              coverages={rfpCoverages}
              classesWithoutCoverages={classesWithoutCoverages}
              onSelectionChange={handleSelectionChange}
              onGridReady={handleGridReady}
              onCellValueChanged={handleCellValueChanged}
              onHeaderCheckboxClick={handleHeaderCheckboxClick}
            />
          </Box>
        </Box>
      </Box>
      <Stack
        direction="row"
        spacing={2}
        sx={{ justifyContent: "flex-end", px: 3, pb: 3, pt: 2 }}
      >
        <Button variant="tertiary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={batchUpdateMutation.isPending || !isValid}
        >
          {batchUpdateMutation.isPending ? "Saving..." : "Save"}
        </Button>
      </Stack>
    </>
  );
};

export default ClassManagerTab;
